import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_volunteer-dispatch-6/artifacts/8p87dbov_hope-to-home-350x66.png";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.";
      setError(detail);
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen surface-public grain flex flex-col items-center justify-center px-4">
      <Link to="/login" className="mb-8 focus-ring rounded-md" data-testid="forgot-back-to-login-link">
        <img src={LOGO_URL} alt="Hope to Home" className="h-12 w-auto" />
      </Link>
      <div className="w-full max-w-sm card-elevated rounded-xl p-7 sm:p-8" data-testid="forgot-password-card">
        {sent ? (
          <div className="text-center" data-testid="forgot-success-panel">
            <div className="mx-auto h-14 w-14 rounded-full bg-brand-green/15 text-brand-green-dark flex items-center justify-center mb-3">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Check your inbox</h1>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              A password-reset link has been sent to <span className="font-medium text-slate-900">{email}</span>. It&apos;s good for 60 minutes.
            </p>
            <div className="text-xs text-slate-500 mt-4 space-y-2 text-left bg-slate-50 rounded-lg p-3">
              <p className="font-semibold text-slate-700">Not seeing it in a couple minutes?</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Check your spam / junk folder.</li>
                <li>Still stuck? Ask your organizer — they can send you a reset link directly from the admin dashboard.</li>
              </ul>
            </div>
            <Link to="/login" className="mt-6 inline-flex items-center text-sm font-semibold text-brand-blue hover:underline" data-testid="forgot-return-to-login-link">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Forgot password?</h1>
            <p className="text-sm text-slate-500 mt-1.5">
              Enter your email and we&apos;ll send you a link to set a new password.
            </p>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div>
                <Label htmlFor="forgot-email" className="text-sm font-semibold">Email</Label>
                <Input
                  id="forgot-email"
                  data-testid="forgot-email-input"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1.5 text-base min-h-[44px]"
                />
              </div>
              {error && (
                <div data-testid="forgot-error-panel" className="rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm p-3 leading-relaxed">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                data-testid="forgot-submit-button"
                disabled={submitting}
                className="w-full min-h-[48px] bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold"
              >
                <Mail className="w-4 h-4 mr-2" />
                {submitting ? "Sending…" : "Email me a reset link"}
              </Button>
            </form>
            <p className="text-xs text-slate-500 mt-6 text-center">
              Remembered it?{" "}
              <Link to="/login" className="text-brand-blue font-semibold hover:underline" data-testid="forgot-to-login-link">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
